import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import maxSizeModifier from 'popper-max-size-modifier';
import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { Position } from 'react-avatar-editor';
import AvatarEditor from 'react-avatar-editor';
import type { Banner } from './BannerModalContent';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import PopOver from './deprecated/PopOver';
import * as styles from './ImageEditor.module.scss';

const JPEG_CONTENT_TYPE = 'image/jpeg';

function getFileFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<File | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        const file = blob
          ? new File([blob], 'banner.jpg', { type: JPEG_CONTENT_TYPE })
          : null;

        resolve(file);
      },
      JPEG_CONTENT_TYPE,
      0.9,
    );
  });
}

function useOnWheel(
  ref: React.RefObject<HTMLElement>,
  onWheel: (e: WheelEvent) => void,
  opts: { skip: boolean },
) {
  const onWheelRef = useRef(onWheel);

  useLayoutEffect(() => {
    onWheelRef.current = onWheel;
  });

  useEffect(() => {
    if (opts.skip) return undefined;

    const container = ref.current;

    // We can't use React's `onWheel` prop because of a backwards-incompatible
    // change in Chrome 73: https://github.com/facebook/react/issues/14856
    const handleWrapperWheel = (e: WheelEvent) => {
      e.preventDefault();
      onWheelRef.current(e);
    };

    container?.addEventListener('wheel', handleWrapperWheel, {
      passive: false,
    });

    return () => {
      container?.removeEventListener('wheel', handleWrapperWheel);
    };
  }, [opts.skip, ref]);
}

const DEFAULT_SCALE = 1.0;

interface ImageEditorProps {
  positioning?: boolean;
  value: Banner;
  src: string | null;
  width: number;
  height: number;
  onImageLoad?: (file: File | null) => void;
  onScaleChange?: (scale: number) => void;
  onPositionChange?: (position: Position) => void;
}

export interface ImageEditorRef {
  getFile(): Promise<File | null>;
}

const ImageEditor = React.forwardRef<ImageEditorRef, ImageEditorProps>(
  (
    {
      src,
      value,
      width,
      height,
      positioning,
      onImageLoad,
      onScaleChange,
      onPositionChange,
    },
    ref,
  ) => {
    const [scale, setScale] = useState<{ value: number; touched: boolean }>({
      value: DEFAULT_SCALE,
      touched: false,
    });

    const onScaleChangeRef = useRef(onScaleChange);
    useLayoutEffect(() => {
      onScaleChangeRef.current = onScaleChange;
    });

    useEffect(() => {
      if (scale.touched) {
        onScaleChangeRef.current?.(scale.value);
      }
    }, [scale]);

    useEffect(() => {
      setScale({ value: DEFAULT_SCALE, touched: false });
    }, [value]);

    const avatarEditorRef = useRef<AvatarEditor>(null);

    const getFile = () => {
      const editor = avatarEditorRef.current;
      if (editor) {
        return getFileFromCanvas(editor.getImageScaledToCanvas());
      }
      return Promise.resolve(null);
    };

    useImperativeHandle(ref, () => ({
      getFile,
    }));

    const editorContainerRef = useRef<HTMLDivElement>(null);

    useOnWheel(
      editorContainerRef,
      (e) => {
        setScale((current) => ({
          value: Math.max(current.value + e.deltaY / 1000, 1.0),
          touched: true,
        }));
      },
      {
        skip: !positioning || value.image === src,
      },
    );

    return (
      <div
        className={styles.container}
        style={{ aspectRatio: `${width}/${height}` }}
      >
        {value.image === src && !positioning ? (
          <div
            className={styles.image}
            style={{ backgroundImage: `url(${value.image})` }}
          />
        ) : (
          <div
            className={classNames(styles.editorContainer, {
              [styles.disabled]: !positioning,
            })}
            ref={editorContainerRef}
          >
            <AvatarEditor
              border={0}
              crossOrigin="anonymous"
              image={value.image}
              ref={avatarEditorRef}
              scale={scale.value}
              height={height}
              width={width}
              onImageReady={async () => {
                if (!onImageLoad) return;
                const file = await getFile();
                onImageLoad(file);
              }}
              onPositionChange={onPositionChange}
            />
          </div>
        )}
      </div>
    );
  },
);

export const DragZoomInfoPill = () => {
  return (
    <div className={styles.dragZoomInfoPill}>Drag & zoom image to move</div>
  );
};

const MAX_WIDTH = 485;
const MAX_HEIGHT = 540;

const SCREEN_EDGE_PADDING = 12;

interface ImageEditorPopOverProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  placement: Placement;
  children: React.ReactNode;
  content: React.ReactNode;
  preventCloseOnClickOutside: (e: Node) => boolean;
}

export const ImageEditorPopOver = ({
  open,
  onOpenChange,
  placement,
  children,
  content,
  preventCloseOnClickOutside,
}: ImageEditorPopOverProps) => {
  const [contentStyle, setContentStyle] = useState<React.CSSProperties>({
    height: MAX_HEIGHT,
    width: MAX_WIDTH,
  });

  return (
    <PopOver
      popOver={
        <ClickOutsideHandler
          onClickOutside={(e) => {
            if (
              !(e.target instanceof Node) ||
              !preventCloseOnClickOutside?.(e.target)
            ) {
              onOpenChange(false);
            }
          }}
        >
          <PopOver.Container fullHeight width="auto" noPadding>
            <div
              style={contentStyle}
              className={styles.imageEditorPopOverContent}
            >
              {content}
            </div>
          </PopOver.Container>
        </ClickOutsideHandler>
      }
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { ...maxSizeModifier, options: { padding: SCREEN_EDGE_PADDING } },
        { name: 'flip', enabled: false },
        {
          name: 'applySize',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['maxSize'],
          fn: ({ state }) => {
            if (open) {
              const maxSize = state.modifiersData.maxSize as {
                width: number;
                height: number;
              };
              const width = Math.min(MAX_WIDTH, maxSize.width);
              const nextContentStyle = {
                height: Math.floor(Math.min(MAX_HEIGHT, maxSize.height)),
                width: Math.floor(width),
              };

              setContentStyle((current) =>
                _.isEqual(current, nextContentStyle)
                  ? current
                  : nextContentStyle,
              );
            }
          },
        },
      ]}
      popOverPortal={document.body}
      arrow={<PopOver.Container.Arrow />}
      placement={placement}
      preventCloseOnContentClick
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
    </PopOver>
  );
};

export default ImageEditor;
